/**
 * UI-only display helpers for expense/ROI empty states.
 *
 * When expense = 0, it means "no data entered" (not "free marketing").
 * These helpers render user-friendly placeholders on screen.
 * Export always uses raw numeric values — see exportToExcel.
 */

import { format } from "date-fns"

import { formatCurrency } from "./utils"

/**
 * Render an inclusive date range from yyyy-MM-dd strings. Omits the start
 * year only when both endpoints fall in the same calendar year — otherwise
 * prints both years, so a 2024-01-01 → 2025-09-05 range reads as
 * «Jan 1, 2024 – Sep 5, 2025» instead of the misleading «Jan 1 – Sep 5, 2025».
 *
 * Inputs are interpreted at local midnight to match the day picker's
 * timezone-naive contract; for server-side absolute timestamps with an
 * explicit display timezone, use `formatDateRangeLabelInTimezone`.
 */
export function formatYmdDateRangeLabel(from: string, to: string): string {
  const f = new Date(from + "T00:00:00")
  const t = new Date(to + "T00:00:00")
  const sameYear = f.getFullYear() === t.getFullYear()
  const fromPattern = sameYear ? "MMM d" : "MMM d, yyyy"
  return `${format(f, fromPattern)} – ${format(t, "MMM d, yyyy")}`
}

/**
 * Timezone-aware variant of `formatDateRangeLabel` — needed when the range
 * boundaries are server-side absolute timestamps and the year boundary must
 * be computed in the integration's display timezone, not the browser's.
 */
export function formatDateRangeLabelInTimezone(
  from: Date,
  to: Date,
  timeZone: string,
): string {
  const yearFmt = new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone })
  const sameYear = yearFmt.format(from) === yearFmt.format(to)
  const startOpts: Intl.DateTimeFormatOptions = sameYear
    ? { month: "short", day: "numeric", timeZone }
    : { month: "short", day: "numeric", year: "numeric", timeZone }
  const endOpts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }
  return `${from.toLocaleDateString("en-US", startOpts)} – ${to.toLocaleDateString("en-US", endOpts)}`
}

/**
 * Format ROI for display. Returns `—` when expense is zero
 * (ROI is meaningless without expense data).
 */
export function formatRoiDisplay(roi: number | undefined, expense: number | undefined): string {
  if (!expense) return "—"
  return `${(roi ?? 0).toFixed(1)}%`
}

/**
 * Format expense for display. Returns `not set` when zero.
 */
export function formatExpenseDisplay(expense: number | undefined): string {
  if (!expense) return "not set"
  return formatCurrency(expense)
}

/**
 * Format cost-per-order for display. Returns `—` when expense is zero.
 */
export function formatCostPerOrderDisplay(costPerOrder: number | undefined, expense: number | undefined): string {
  if (!expense) return "—"
  return formatCurrency(costPerOrder ?? 0)
}

/**
 * Whether ROI badge should use positive (green) styling.
 * Only green when there IS expense data and ROI >= 0.
 */
export function isRoiPositive(roi: number | undefined, expense: number | undefined): boolean {
  if (!expense) return false
  return (roi ?? 0) >= 0
}

/**
 * Whether an expense value represents "no data" (empty state).
 */
export function isExpenseEmpty(expense: number | undefined): boolean {
  return !expense
}
